import { useCallback, useEffect, useState } from "react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type SeoAlertSettings = {
  alerts_enabled: boolean;
  impressions_threshold: number;
  impressions_window_days: number;
  alert_on_indexing: boolean;
  alert_on_sitemap: boolean;
  cooldown_hours: number;
  target_url: string;
  selected_site_url: string | null;
  extra_recipients: string[];
  last_checked_at: string | null;
  last_alerted_at: string | null;
};

const DEFAULTS: SeoAlertSettings = {
  alerts_enabled: true,
  impressions_threshold: 50,
  impressions_window_days: 7,
  alert_on_indexing: true,
  alert_on_sitemap: true,
  cooldown_hours: 24,
  target_url: "https://elarahealth.life/",
  selected_site_url: null,
  extra_recipients: [],
  last_checked_at: null,
  last_alerted_at: null,
};

export function useSeoAlertSettings() {
  const [settings, setSettings] = useState<SeoAlertSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await supabase
      .from("seo_alert_settings")
      .select("*")
      .eq("id", "default")
      .maybeSingle();
    if (loadError) {
      setError(loadError.message);
      setSettings(null);
    } else {
      setSettings(data ? ({ ...DEFAULTS, ...data } as SeoAlertSettings) : DEFAULTS);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async (patch: Partial<SeoAlertSettings>) => {
    setSaving(true);
    setError(null);
    const { data, error: saveError } = await supabase
      .from("seo_alert_settings")
      .update(patch)
      .eq("id", "default")
      .select("*")
      .maybeSingle();
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return false;
    }
    if (data) setSettings({ ...DEFAULTS, ...data } as SeoAlertSettings);
    return true;
  }, []);

  const runCheckNow = useCallback(async () => {
    setRunning(true);
    setError(null);
    const { data, error: invokeError } = await supabase.functions.invoke("check-seo-alerts", {
      body: { force: true },
    });
    setRunning(false);
    if (invokeError) {
      const details =
        invokeError instanceof FunctionsHttpError
          ? await invokeError.context.text()
          : invokeError.message;
      setError(details);
      return null;
    }
    await load();
    return data as { alerted?: boolean; issues?: Array<{ title: string }>; reason?: string } | null;
  }, [load]);

  return { settings, loading, saving, running, error, reload: load, save, runCheckNow };
}
